import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { getLoan, cancelLoan } from '../../api/loans';
import { useAuth } from '../../auth/useAuth';
import { isCustomerAdminFor } from '../../auth/permissions';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { StatusBadge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { ApproveLoanModal } from './ApproveLoanModal';
import { RejectLoanModal } from './RejectLoanModal';
import { CheckoutLoanModal } from './CheckoutLoanModal';
import { CheckinLoanModal } from './CheckinLoanModal';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm text-slate-800">{value ?? '-'}</p>
    </div>
  );
}

function fullName(u: { firstName: string | null; lastName: string | null; email: string }) {
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
}

export function LoanDetailPage() {
  const { loanId } = useParams<{ loanId: string }>();
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<'approve' | 'reject' | 'checkout' | 'checkin' | null>(null);

  const loanQuery = useQuery({ queryKey: ['loan', loanId], queryFn: () => getLoan(loanId!), enabled: !!loanId });

  const cancelMutation = useMutation({
    mutationFn: () => cancelLoan(loanId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['loan', loanId] });
      queryClient.invalidateQueries({ queryKey: ['loans-queue'] });
      showToast('Loan request cancelled.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not cancel this loan request.')),
  });

  if (!loanId) return null;

  if (loanQuery.isLoading || !loanQuery.data) {
    return (
      <div className="py-14 text-center">
        <Spinner />
      </div>
    );
  }

  const loan = loanQuery.data;
  const isMspAdmin = user?.isMspAdmin ?? false;
  const isRequester = user?.id === loan.requestedById;
  const isSigningAuthorityHere = user?.siteRoles.some((r) => r.siteId === loan.siteId && r.role === 'SIGNING_AUTHORITY') ?? false;
  const isOrgAdminHere = isCustomerAdminFor(user, loan.site.customerOrganisationId);

  const canApprove = loan.status === 'PENDING_APPROVAL' && (isMspAdmin || isSigningAuthorityHere);
  const canCancel = (loan.status === 'PENDING_APPROVAL' || loan.status === 'APPROVED') && (isMspAdmin || isRequester);
  const canCheckout = loan.status === 'APPROVED' && (isMspAdmin || isOrgAdminHere);
  const canCheckin = loan.status === 'ACTIVE' && (isMspAdmin || isOrgAdminHere);

  const isOverdue = loan.status === 'ACTIVE' && new Date(loan.dueDate).getTime() < Date.now();

  return (
    <div>
      <PageHeader
        breadcrumb={
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Link to="/loans" className="hover:text-slate-600">
              Loans
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span>{loan.asset.orderLine?.customerCatalogItem?.name ?? 'Loan'}</span>
          </div>
        }
        title={loan.asset.orderLine?.customerCatalogItem?.name ?? 'Loan'}
        actions={
          <div className="flex items-center gap-2">
            {isOverdue && <span className="text-xs font-medium text-red-600">Overdue</span>}
            <StatusBadge status={loan.status} kind="loan" />
          </div>
        }
      />

      <div className="flex flex-col gap-6">
        {error && <Alert tone="error">{error}</Alert>}

        <Card>
          <CardHeader>
            <span className="text-sm font-medium text-slate-600">Details</span>
          </CardHeader>
          <CardBody className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Site" value={loan.site.name} />
            <Field label="Requested by" value={fullName(loan.requestedBy)} />
            <Field label="Due back" value={new Date(loan.dueDate).toLocaleDateString()} />
            <Field label="Serial number" value={loan.asset.serialNumber} />
            <Field label="Requested" value={new Date(loan.createdAt).toLocaleString()} />
            <Field label="Notes" value={loan.notes} />
          </CardBody>

          {(loan.approvedBy || loan.rejectionReason || loan.checkedOutBy || loan.checkedInBy) && (
            <CardBody className="grid grid-cols-2 gap-4 border-t border-slate-100 sm:grid-cols-3">
              {loan.approvedBy && loan.approvedAt && (
                <Field label="Approved by" value={`${fullName(loan.approvedBy)} - ${new Date(loan.approvedAt).toLocaleString()}`} />
              )}
              {loan.rejectionReason && <Field label="Rejection reason" value={loan.rejectionReason} />}
              {loan.checkedOutBy && loan.checkedOutAt && (
                <Field label="Checked out by" value={`${fullName(loan.checkedOutBy)} - ${new Date(loan.checkedOutAt).toLocaleString()}`} />
              )}
              {loan.checkedInBy && loan.checkedInAt && (
                <Field label="Checked in by" value={`${fullName(loan.checkedInBy)} - ${new Date(loan.checkedInAt).toLocaleString()}`} />
              )}
            </CardBody>
          )}

          {(canApprove || canCancel || canCheckout || canCheckin) && (
            <div className="flex flex-wrap gap-2 border-t border-slate-100 px-5 py-3">
              {canApprove && (
                <>
                  <Button size="sm" variant="danger" onClick={() => setAction('reject')}>
                    Reject
                  </Button>
                  <Button size="sm" onClick={() => setAction('approve')}>
                    Approve
                  </Button>
                </>
              )}
              {canCheckout && (
                <Button size="sm" onClick={() => setAction('checkout')}>
                  Check out
                </Button>
              )}
              {canCheckin && (
                <Button size="sm" onClick={() => setAction('checkin')}>
                  Check in
                </Button>
              )}
              {canCancel && (
                <Button size="sm" variant="secondary" isLoading={cancelMutation.isPending} onClick={() => cancelMutation.mutate()}>
                  Cancel request
                </Button>
              )}
            </div>
          )}
        </Card>
      </div>

      {action === 'approve' && <ApproveLoanModal loan={loan} open onClose={() => setAction(null)} />}
      {action === 'reject' && <RejectLoanModal loan={loan} open onClose={() => setAction(null)} />}
      {action === 'checkout' && <CheckoutLoanModal loan={loan} open onClose={() => setAction(null)} />}
      {action === 'checkin' && <CheckinLoanModal loan={loan} open onClose={() => setAction(null)} />}
    </div>
  );
}
